import React from 'react';

import { RefusedReasonDialog } from '@lumapps/designer-content-editor/components/ContentEditorTopbar/RefusedReasonDialog';
import { ShowRequestRevisionDetailsLink } from '@lumapps/designer-content-editor/components/ContentEditorTopbar/ShowRequestRevisionDetailsLink';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { ColorPalette, FlexBox, Icon, Text, TextProps, Typography } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';
import { User } from '@lumapps/user/types';

import { VALIDATION_STATUS, WorkflowValidationMap } from '../../constants';
import { CONTENTS } from '../../keys';
import { LegacyContentStatus, ReadModeContent } from '../../types';

interface ContentLastRevisionProps {
    /** The content status */
    contentStatus: ReadModeContent['status'];
    /** Last revision status of a content to know if a draft is in progress or a validation workflow is ongoing */
    lastRevisionStatus?: NonNullable<ReadModeContent['lastRevision']>['status'];
    refusedInformation?: { reason: string; createdBy: User; createdAt: string };
}

/** Component used to display the last version state (draft in progress, submitted for review or version refused) */
export const ContentLastRevision: React.FC<ContentLastRevisionProps> = ({
    contentStatus,
    lastRevisionStatus,
    refusedInformation,
}) => {
    const { translateKey } = useTranslate();
    const [isRefusedReasonDialogOpen, , closeRefusedReasonDialog, openRefusedReasonDialog] = useBooleanState(false);

    const revisionHint = React.useMemo<
        | {
              label: string;
              icon?: string;
              textColor?: { color?: TextProps['color']; colorVariant: TextProps['colorVariant'] };
          }
        | undefined
    >(() => {
        if (lastRevisionStatus === LegacyContentStatus.toValidate) {
            return WorkflowValidationMap[VALIDATION_STATUS.TO_VALIDATE];
        }
        if (lastRevisionStatus === LegacyContentStatus.refused) {
            return WorkflowValidationMap[VALIDATION_STATUS.REFUSED];
        }
        // "Draft in progress": only when content is LIVE but lastRevision is still DRAFT.
        if (contentStatus === LegacyContentStatus.live && lastRevisionStatus === LegacyContentStatus.draft) {
            return {
                label: CONTENTS.UNPUBLISHED_SAVED_CHANGES_WARNING_MESSAGE,
                textColor: { color: ColorPalette.grey, colorVariant: 'N' },
            };
        }
        return undefined;
    }, [contentStatus, lastRevisionStatus]);

    if (!revisionHint) {
        return null;
    }

    return (
        <FlexBox gap="tiny" orientation="horizontal" hAlign="center">
            {revisionHint.icon && (
                <Icon icon={revisionHint.icon} color={ColorPalette.grey} {...revisionHint.textColor} />
            )}
            {lastRevisionStatus === LegacyContentStatus.refused && refusedInformation ? (
                <>
                    <ShowRequestRevisionDetailsLink onClick={openRefusedReasonDialog} />
                    <RefusedReasonDialog
                        isOpen={isRefusedReasonDialogOpen}
                        closeDialog={closeRefusedReasonDialog}
                        refusedReason={refusedInformation.reason}
                        updatedBy={refusedInformation.createdBy}
                        updatedAt={refusedInformation.createdAt}
                    />
                </>
            ) : (
                <Text
                    as="span"
                    truncate
                    color={ColorPalette.grey}
                    {...revisionHint.textColor}
                    typography={Typography.body1}
                >
                    {translateKey(revisionHint.label)}
                </Text>
            )}
        </FlexBox>
    );
};
